// A type of promise-like that resolves synchronously and supports only one observer
const _Pact = /*#__PURE__*/(function() {
	function _Pact() {}
	_Pact.prototype.then = function(onFulfilled, onRejected) {
		const result = new _Pact();
		const state = this.s;
		if (state) {
			const callback = state & 1 ? onFulfilled : onRejected;
			if (callback) {
				try {
					_settle(result, 1, callback(this.v));
				} catch (e) {
					_settle(result, 2, e);
				}
				return result;
			} else {
				return this;
			}
		}
		this.o = function(_this) {
			try {
				const value = _this.v;
				if (_this.s & 1) {
					_settle(result, 1, onFulfilled ? onFulfilled(value) : value);
				} else if (onRejected) {
					_settle(result, 1, onRejected(value));
				} else {
					_settle(result, 2, value);
				}
			} catch (e) {
				_settle(result, 2, e);
			}
		};
		return result;
	};
	return _Pact;
})();

// Settles a pact synchronously
function _settle(pact, state, value) {
	if (!pact.s) {
		if (value instanceof _Pact) {
			if (value.s) {
				if (state & 1) {
					state = value.s;
				}
				value = value.v;
			} else {
				value.o = _settle.bind(null, pact, state);
				return;
			}
		}
		if (value && value.then) {
			value.then(_settle.bind(null, pact, state), _settle.bind(null, pact, 2));
			return;
		}
		pact.s = state;
		pact.v = value;
		const observer = pact.o;
		if (observer) {
			observer(pact);
		}
	}
}

function _isSettledPact(thenable) {
	return thenable instanceof _Pact && thenable.s & 1;
}

// Asynchronously iterate through an object that has a length property, passing the index as the first argument to the callback (even as the length property changes)
function _forTo(array, body, check) {
	var i = -1, pact, reject;
	function _cycle(result) {
		try {
			while (++i < array.length && (!check || !check())) {
				result = body(i);
				if (result && result.then) {
					if (_isSettledPact(result)) {
						result = result.v;
					} else {
						result.then(_cycle, reject || (reject = _settle.bind(null, pact = new _Pact(), 2)));
						return;
					}
				}
			}
			if (pact) {
				_settle(pact, 1, result);
			} else {
				pact = result;
			}
		} catch (e) {
			_settle(pact || (pact = new _Pact()), 2, e);
		}
	}
	_cycle();
	return pact;
}

const _iteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.iterator || (Symbol.iterator = Symbol("Symbol.iterator"))) : "@@iterator";

// Asynchronously iterate through an object's values
// Uses for...of if the runtime supports it, otherwise iterates until length on a copy
function _forOf(target, body, check) {
	if (typeof target[_iteratorSymbol] === "function") {
		var iterator = target[_iteratorSymbol](), step, pact, reject;
		function _cycle(result) {
			try {
				while (!(step = iterator.next()).done && (!check || !check())) {
					result = body(step.value);
					if (result && result.then) {
						if (_isSettledPact(result)) {
							result = result.v;
						} else {
							result.then(_cycle, reject || (reject = _settle.bind(null, pact = new _Pact(), 2)));
							return;
						}
					}
				}
				if (pact) {
					_settle(pact, 1, result);
				} else {
					pact = result;
				}
			} catch (e) {
				_settle(pact || (pact = new _Pact()), 2, e);
			}
		}
		_cycle();
		if (iterator.return) {
			var _fixup = function(value) {
				try {
					if (!step.done) {
						iterator.return();
					}
				} catch(e) {
				}
				return value;
			};
			if (pact && pact.then) {
				return pact.then(_fixup, function(e) {
					throw _fixup(e);
				});
			}
			_fixup();
		}
		return pact;
	}
	// No support for Symbol.iterator
	if (!("length" in target)) {
		throw new TypeError("Object is not iterable");
	}
	// Handle live collections properly
	var values = [];
	for (var i = 0; i < target.length; i++) {
		values.push(target[i]);
	}
	return _forTo(values, function(i) { return body(values[i]); }, check);
}

const _asyncIteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.asyncIterator || (Symbol.asyncIterator = Symbol("Symbol.asyncIterator"))) : "@@asyncIterator";

/**
 * Passed to event listeners as an argument when dispatching an event.
 */
var EventDispatcherContext = /*#__PURE__*/function () {
  function EventDispatcherContext(eventName) {
    this.isPropagationStopped = false;
    this.eventName = eventName;
  }

  var _proto = EventDispatcherContext.prototype;

  _proto.stopPropagation = function stopPropagation() {
    this.isPropagationStopped = true;
  };

  return EventDispatcherContext;
}();
var EventDispatcher = /*#__PURE__*/function () {
  function EventDispatcher() {
    this.listeners = new Map();
    this.orderedListeners = new Map(); // a cache
  }

  var _proto2 = EventDispatcher.prototype;

  _proto2.dispatch = function dispatch(eventName, eventData) {
    try {
      var _exit2 = false;

      var _this2 = this;

      var listeners = _this2.getListeners(eventName);

      var context = new EventDispatcherContext(eventName);

      var _temp2 = _forOf(listeners, function (listener) {
        return Promise.resolve(listener(eventData, context)).then(function () {
          if (context.isPropagationStopped) {
            _exit2 = true;
            return context;
          }
        });
      }, function () {
        return _exit2;
      });

      return Promise.resolve(_temp2 && _temp2.then ? _temp2.then(function (_result) {
        return _exit2 ? _result : context;
      }) : _exit2 ? _temp2 : context);
    } catch (e) {
      return Promise.reject(e);
    }
  }
  /**
   * Greater priority-number listeners are called first.
   * Listeners with the same priority are called in the order they were registered.
   */
  ;

  _proto2.addListener = function addListener(eventName, listener, priority) {
    if (priority === void 0) {
      priority = 0;
    }

    var tuples = this.listeners.get(eventName) || [];
    tuples.push([priority, listener]);
    this.listeners.set(eventName, tuples); // Reset the cache:

    this.orderedListeners["delete"](eventName);
  };

  _proto2.getListeners = function getListeners(eventName) {
    if (this.orderedListeners.has(eventName)) {
      // Forced type because TypeScript doesn't recognize the previous `has` condition as a type-guard.
      // @see https://github.com/microsoft/TypeScript/issues/13086
      return this.orderedListeners.get(eventName);
    }

    var orderedListeners = this.getOrderedListeners(eventName);
    this.orderedListeners.set(eventName, orderedListeners);
    return orderedListeners;
  };

  _proto2.getOrderedListeners = function getOrderedListeners(eventName) {
    var tuples = this.listeners.get(eventName) || [];
    return tuples.sort(function (a, b) {
      if (a[0] > b[0]) {
        return -1;
      }

      if (a[0] < b[0]) {
        return 1;
      }

      return 0;
    }).map(function (t) {
      return t[1];
    });
  };

  return EventDispatcher;
}();

export { EventDispatcher, EventDispatcherContext };
//# sourceMappingURL=ts-event-dispatcher.esm.js.map
